// File: clientapp/src/pages/SecurityUserDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Grid,
    TextField,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    OutlinedInput,
    InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';

function validatePassword(password) {
    return {
        length: password.length >= 6,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        digit: /\d/.test(password)
    };
}

export default function SecurityUserDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === "new";

    // Main user detail state
    const [loading, setLoading] = useState(!isNew);
    const [userDetail, setUserDetail] = useState({
        email: "",
        encompassUsername: "", 
        mustChangePassword: false,
        status: "Active",
        groups: [],
        features: []
    });
    const [saveMessage, setSaveMessage] = useState("");

    // States for managing group memberships (unchanged)
    const [groupFilter, setGroupFilter] = useState("");
    const [groupPage, setGroupPage] = useState(0);
    const [groupRowsPerPage, setGroupRowsPerPage] = useState(20);
    const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [groupSearch, setGroupSearch] = useState("");
    const [availPage, setAvailPage] = useState(0);
    const [availRowsPerPage, setAvailRowsPerPage] = useState(10);

    // For new-user creation: password field and visibility toggle
    const [newPassword, setNewPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [newPasswordValidations, setNewPasswordValidations] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        digit: false
    });

    // For existing user password change dialog
    const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
    const [changePassword, setChangePassword] = useState("");
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [changePasswordValidations, setChangePasswordValidations] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        digit: false
    });

    useEffect(() => {
        if (!isNew) {
            fetchUserDetail();
        }
    }, [id, isNew]);

    useEffect(() => {
        setNewPasswordValidations(validatePassword(newPassword));
    }, [newPassword]);

    useEffect(() => {
        setChangePasswordValidations(validatePassword(changePassword));
    }, [changePassword]);

    async function fetchUserDetail() {
        try {
            const response = await fetch(`/api/security/users/${id}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setUserDetail(data);
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    const handleSaveUser = async () => {
        try {
            const url = isNew ? `/api/security/users` : `/api/security/users/${id}`;
            const method = isNew ? 'POST' : 'PUT';
            const payload = isNew
                ? {
                    email: userDetail.email,
                    password: newPassword,
                    mustChangePassword: userDetail.mustChangePassword
                }
                : {
                    email: userDetail.email,
                    mustChangePassword: userDetail.mustChangePassword,
                    encompassUsername: userDetail.encompassUsername
                };
            const response = await fetch(url, {
                method,
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                setSaveMessage("User saved successfully.");
                if (isNew) {
                    const created = await response.json();
                    navigate(`/security/users/${created.id}`);
                } else {
                    fetchUserDetail();
                }
            } else {
                setSaveMessage("Failed to save user.");
            }
        } catch (err) {
            console.error(err);
            setSaveMessage("Error saving user.");
        }
    };

    // --- Group Management functions (unchanged) ---
    const handleRemoveGroup = async (groupId) => {
        if (window.confirm("Remove this group from the user?")) {
            try {
                const response = await fetch(`/api/security/users/${id}/groups/${groupId}`, {
                    method: 'DELETE',
                    credentials: 'include'
                });
                if (response.ok) {
                    fetchUserDetail();
                }
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleOpenAddGroupDialog = () => {
        setAddGroupDialogOpen(true);
        setAvailPage(0);
        setGroupSearch("");
        fetchAvailableGroups("");
    };

    const handleCloseAddGroupDialog = () => {
        setAddGroupDialogOpen(false);
        setGroupSearch("");
        setAvailableGroups([]);
        setAvailPage(0);
    };

    async function fetchAvailableGroups(searchTerm) {
        try {
            const response = await fetch(`/api/security/availableGroups?userId=${id}&search=${encodeURIComponent(searchTerm)}`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableGroups(data);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const handleAddGroup = async (groupId) => {
        try {
            const response = await fetch(`/api/security/users/${id}/groups`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ groupId })
            });
            if (response.ok) {
                fetchUserDetail();
            }
        } catch (err) {
            console.error(err);
        }
        handleCloseAddGroupDialog();
    };

    const handleGroupPageChange = (event, newPage) => {
        setGroupPage(newPage);
    };

    const handleGroupRowsPerPageChange = (event) => {
        setGroupRowsPerPage(parseInt(event.target.value, 10));
        setGroupPage(0);
    };

    const filteredGroups = userDetail.groups.filter(g => g.name.toLowerCase().includes(groupFilter.toLowerCase()));
    const paginatedGroups = filteredGroups.slice(groupPage * groupRowsPerPage, groupPage * groupRowsPerPage + groupRowsPerPage);

    // --- Change Password Dialog for Existing User ---
    const handleOpenChangePasswordDialog = () => {
        setChangePassword("");
        setShowChangePassword(false);
        setChangePasswordDialogOpen(true);
    };

    const handleCloseChangePasswordDialog = () => {
        setChangePasswordDialogOpen(false);
    };

    const handleSubmitChangePassword = async () => {
        const validations = validatePassword(changePassword);
        const allValid = Object.values(validations).every(v => v);
        if (!allValid) {
            alert("New password does not meet the requirements.");
            return;
        }
        try {
            const response = await fetch(`/api/security/users/${id}/changePassword`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPassword: changePassword })
            });
            if (response.ok) {
                handleCloseChangePasswordDialog();
                fetchUserDetail();
            } else {
                alert("Failed to change password.");
            }
        } catch (err) {
            console.error(err);
            alert("Error changing password.");
        }
    };

    if (loading) {
        return (
            <Container>
                <Typography variant="body1">Loading...</Typography>
            </Container>
        );
    }

    return (
        <Container>
            {/* Header with Back, red lock icon if user is locked (using status), Change Password button, and Save icon */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <Tooltip title="Back to User List">
                        <IconButton color="primary" onClick={() => navigate('/security/users')}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="h5" ml={1}>User Detail</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    {userDetail.status === "Locked" && (
                        <Tooltip title="User is locked. Click to unlock.">
                            <IconButton
                                color="error"
                                onClick={async () => {
                                    await fetch(`/api/security/users/unlock/${id}`, { method: 'POST', credentials: 'include' });
                                    fetchUserDetail();
                                }}
                                sx={{ mr: 1 }}
                            >
                                <LockIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!isNew && (
                        <Button variant="outlined" onClick={handleOpenChangePasswordDialog} sx={{ mr: 1 }}>
                            Change Password
                        </Button>
                    )}
                    <Tooltip title="Save Changes">
                        <IconButton color="primary" onClick={handleSaveUser}>
                            <SaveIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Basic Information Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} >
                        <TextField
                            label="Email"
                            value={userDetail.email}
                            onChange={e => setUserDetail({ ...userDetail, email: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            label="Encompass Username"
                            value={userDetail.encompassUsername || ""}
                            onChange={e => setUserDetail({ ...userDetail, encompassUsername: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    {/* Must Change Password Checkbox always appears under email */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={userDetail.mustChangePassword}
                                    onChange={e => setUserDetail({ ...userDetail, mustChangePassword: e.target.checked })}
                                    color="primary"
                                />
                            }
                            label="Must change password at next login"
                        />
                    </Grid>
                    {/* For new users, display the password field */}
                    {isNew && (
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel htmlFor="new-user-password">Password</InputLabel>
                                <OutlinedInput
                                    id="new-user-password"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={e => setNewPassword(e.target.value)}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                                {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Password"
                                />
                            </FormControl>
                            <Box mt={1}>
                                <Typography variant="caption" color={newPasswordValidations.length ? 'green' : 'red'}>
                                    • At least 6 characters
                                </Typography><br />
                                <Typography variant="caption" color={newPasswordValidations.uppercase ? 'green' : 'red'}>
                                    • At least one uppercase letter
                                </Typography><br />
                                <Typography variant="caption" color={newPasswordValidations.lowercase ? 'green' : 'red'}>
                                    • At least one lowercase letter
                                </Typography><br />
                                <Typography variant="caption" color={newPasswordValidations.digit ? 'green' : 'red'}>
                                    • At least one digit
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                    {saveMessage && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="primary">{saveMessage}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* Group Memberships Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Group Memberships</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <TextField
                        label="Search Groups"
                        variant="outlined"
                        size="small"
                        value={groupFilter}
                        onChange={e => { setGroupFilter(e.target.value); setGroupPage(0); }}
                    />
                    <Tooltip title="Add Group">
                        <IconButton color="primary" onClick={handleOpenAddGroupDialog}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Group Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedGroups.map(group => (
                            <TableRow key={group.groupId}>
                                <TableCell>{group.name}</TableCell>
                                <TableCell>{group.description}</TableCell>
                                <TableCell>
                                    <Tooltip title="Remove Group">
                                        <IconButton size="small" onClick={() => handleRemoveGroup(group.groupId)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredGroups.length}
                    page={groupPage}
                    onPageChange={handleGroupPageChange}
                    rowsPerPage={groupRowsPerPage}
                    onRowsPerPageChange={handleGroupRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                />
            </Paper>

            {/* Feature Access Section */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Feature Access</Typography>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Feature Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {userDetail.features.map((feature, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{feature.name}</TableCell>
                                <TableCell>{feature.description}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Add Group Dialog */}
            <Dialog open={addGroupDialogOpen} onClose={handleCloseAddGroupDialog} fullWidth maxWidth="md">
                <DialogTitle>Add Group</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Search Available Groups"
                        variant="outlined"
                        fullWidth
                        value={groupSearch}
                        onChange={e => {
                            setGroupSearch(e.target.value);
                            setAvailPage(0);
                            fetchAvailableGroups(e.target.value);
                        }}
                    />
                    <Paper sx={{ maxHeight: 300, overflow: 'auto', mt: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Group Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {availableGroups
                                    .slice(availPage * availRowsPerPage, availPage * availRowsPerPage + availRowsPerPage)
                                    .map(ag => (
                                        <TableRow key={ag.groupId}>
                                            <TableCell>{ag.name}</TableCell>
                                            <TableCell>{ag.description}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Add Group">
                                                    <IconButton size="small" onClick={() => handleAddGroup(ag.groupId)}>
                                                        <AddIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </Paper>
                    <TablePagination
                        component="div"
                        count={availableGroups.length}
                        page={availPage}
                        onPageChange={(e, newPage) => setAvailPage(newPage)}
                        rowsPerPage={availRowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setAvailRowsPerPage(parseInt(e.target.value, 10));
                            setAvailPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 20, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddGroupDialog}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Change Password Dialog for Existing Users */}
            <Dialog open={changePasswordDialogOpen} onClose={handleCloseChangePasswordDialog} fullWidth maxWidth="sm">
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel htmlFor="change-password">New Password</InputLabel>
                        <OutlinedInput
                            id="change-password"
                            type={showChangePassword ? 'text' : 'password'}
                            value={changePassword}
                            onChange={e => setChangePassword(e.target.value)}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowChangePassword(!showChangePassword)} edge="end">
                                        {showChangePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label="New Password"
                        />
                    </FormControl>
                    <Box mt={2}>
                        <Typography variant="body2">Password must meet the following requirements:</Typography>
                        <Box ml={2}>
                            <Typography variant="caption" color={changePasswordValidations.length ? 'green' : 'red'}>
                                • At least 6 characters
                            </Typography><br />
                            <Typography variant="caption" color={changePasswordValidations.uppercase ? 'green' : 'red'}>
                                • At least one uppercase letter
                            </Typography><br />
                            <Typography variant="caption" color={changePasswordValidations.lowercase ? 'green' : 'red'}>
                                • At least one lowercase letter
                            </Typography><br />
                            <Typography variant="caption" color={changePasswordValidations.digit ? 'green' : 'red'}>
                                • At least one digit
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseChangePasswordDialog}>Cancel</Button>
                    <Button onClick={handleSubmitChangePassword} variant="contained" color="primary">Change Password</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}


